import org.w3c.dom.HTMLElement

/**
 * Attaching Gridstack to the dashboard's own grid container.
 *
 * The dashboard is rebuilt more than once per visit: `render()` schedules
 * `initGridStack()` on a timer, and every loader that settles while the grid is
 * still null triggers another full re-render — so several attach attempts can be
 * in flight, some of them belonging to a dashboard instance whose DOM has already
 * been thrown away. This object is the single place that decides what happens in
 * each of those cases, so the rules can be tested without standing up the whole
 * dashboard.
 */
internal object DashboardGrid {

    /** The Gridstack instance already attached to [gridEl], or null if there is none. */
    fun existing(gridEl: HTMLElement): GridStack? {
        val g = gridEl.asDynamic().gridstack
        return if (g == null || g == undefined) null else g.unsafeCast<GridStack>()
    }

    /**
     * Attach Gridstack to [gridEl] with [options], or hand back the instance that
     * is already on it.
     *
     * Two invariants callers depend on:
     *
     *  - **It is this element or nothing.** A dashboard that has been re-rendered,
     *    or one whose container was detached when its tab was left, must never end
     *    up driving a different dashboard's grid.
     *  - **It is idempotent.** Calling twice returns the same instance and leaves
     *    the widgets already on the grid alone; the caller uses [existing] to know
     *    it should skip the rest of its setup instead of populating the grid twice.
     */
    fun attach(gridEl: HTMLElement, options: dynamic): GridStack? =
        existing(gridEl) ?: GridStack.init(options, gridEl)

    /**
     * Every widget on [g] as `{id, x, y, w, h}`, top row first — the layout the
     * dashboard persists.
     *
     * Read off the engine's own nodes, deliberately NOT off `GridStack.save()`.
     * `save()` is a round-trip format, not a record of the layout: it pipes every
     * node through Gridstack's `Utils.removeInternalForSave`, which DELETES `w` when
     * it equals that node's `minW` (or 1) and `h` when it equals its `minH` (or 1),
     * on the understanding that `load()` will reconstruct them from the minimums it
     * is handed back. Every widget sitting at its minimum therefore came out of
     * `save()` with no size at all.
     *
     * Persisting that verbatim is what put `"h": undefined` — and then, after
     * `JSON.stringify`, no height key whatsoever — into the user's saved layout, and
     * it cost two things:
     *
     *  - **The height stopped belonging to the layout.** A widget saved with no
     *    height comes back at whatever `minH` the build that reads it happens to
     *    declare, so raising a widget's minimum silently resizes it in every layout
     *    already saved at the old one — the user never touched it and it is a row
     *    taller on the next visit.
     *  - **No save could ever be recognised as recording nothing.** What was
     *    restored carries a height and what was collected did not, so
     *    [DashboardLayoutStore.isNoOpSave] could not match them: a visit with no
     *    interaction at all still rewrote localStorage and scheduled a server sync,
     *    pushing that stripped layout to the user's other browsers.
     *
     * The engine's nodes always carry real numbers for all four fields, so they are
     * the honest source. `sortNodes()` first, so the order matches what `save()`
     * produced — the JSON has to compare equal to the layout already stored, not
     * merely describe the same widgets.
     */
    fun geometry(g: GridStack): Array<dynamic> {
        val out = js("[]")
        val engine = g.asDynamic().engine ?: return out.unsafeCast<Array<dynamic>>()
        engine.sortNodes()
        engine.nodes.unsafeCast<Array<dynamic>>().forEach { n ->
            val o = js("{}")
            // save() drops the element, engine nodes keep it: gs-id is the fallback
            // for a node whose id never made it onto the node itself.
            o.id = n.id ?: n.el?.getAttribute("gs-id")
            o.x = n.x
            o.y = n.y
            o.w = n.w
            o.h = n.h
            out.push(o)
        }
        return out.unsafeCast<Array<dynamic>>()
    }

    /**
     * Vertical-gravity compaction: every widget except [excludeId] falls "up" to the
     * highest row where its rect doesn't overlap another widget's. Widgets never move
     * horizontally — a widget at column 3 can rise from row 5 to row 2 but never slide
     * to column 10. Used after a drag or a removal instead of Gridstack's own
     * `compact()`, which repacks globally and teleports widgets the user never
     * touched.
     *
     * **The whole pass is one batch, and that is the point.** Gridstack fires a
     * `change` event out of every `update()` that moves a node, and the dashboard
     * persists the layout on `change` — so an unbatched compaction wrote localStorage
     * and scheduled a server sync once per widget it lifted. On a full dashboard that
     * is a double-digit burst of writes from a single drag, every one of them storing
     * a half-finished arrangement in which some widgets have risen and the rest have
     * not; a reload landing inside the burst restores one of those. Batching collapses
     * the pass into the single `change` that describes the finished result.
     *
     * The moves themselves are unaffected: `update()` still applies each new y
     * synchronously, so the collision scan keeps seeing current positions, and the
     * pack Gridstack runs when the batch closes only pulls floating nodes back DOWN
     * toward where they started — never anywhere this loop has just lifted them from.
     */
    fun compactVertically(g: GridStack, excludeId: String?) {
        val engine = g.asDynamic().engine ?: return
        val nodesArr = engine.nodes.unsafeCast<Array<dynamic>>()
        // Snapshot into a sortable list. Sort by (y asc, x asc) so
        // top-row widgets settle first, freeing space for the row below.
        val ordered = nodesArr.toList().sortedWith(
            compareBy(
                { (it.y as? Number)?.toDouble() ?: 0.0 },
                { (it.x as? Number)?.toDouble() ?: 0.0 },
            ),
        )
        g.batchUpdate(true)
        for (n in ordered) {
            val nid = (n.id as? String)
            if (nid != null && nid == excludeId) continue
            val nx = (n.x as? Number)?.toInt() ?: continue
            val ny = (n.y as? Number)?.toInt() ?: continue
            val nw = (n.w as? Number)?.toInt() ?: continue
            val nh = (n.h as? Number)?.toInt() ?: continue
            if (ny <= 0) continue // already at top, nothing to do

            // Find the highest y where this widget's rect doesn't
            // overlap any other widget's current rect.
            var targetY = 0
            while (targetY < ny) {
                val collides = nodesArr.any { other ->
                    if (other === n) return@any false
                    val ox = (other.x as? Number)?.toInt() ?: return@any false
                    val oy = (other.y as? Number)?.toInt() ?: return@any false
                    val ow = (other.w as? Number)?.toInt() ?: return@any false
                    val oh = (other.h as? Number)?.toInt() ?: return@any false
                    val xOverlap = nx < (ox + ow) && ox < (nx + nw)
                    val yOverlap = targetY < (oy + oh) && oy < (targetY + nh)
                    xOverlap && yOverlap
                }
                if (!collides) break
                targetY++
            }
            if (targetY < ny) {
                val el = n.el.unsafeCast<HTMLElement?>() ?: continue
                val opts = js("({})")
                opts.y = targetY
                g.asDynamic().update(el, opts)
            }
        }
        g.batchUpdate(false)
    }
}
